import React, { ForwardedRef, forwardRef, MouseEvent, useEffect, useRef } from 'react';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import styles from './hot-filter-button.module.scss';

type HotFilterButtonProps = {
  label: string;
  dataCy: string;
  onClick: (e: MouseEvent) => void;
  isSelected: boolean;
  isHighlighted?: boolean;
  checkedItemCount?: number;
  isBooleanFilter?: boolean;
};

const HotFilterButton = forwardRef(
  (
    {
      dataCy,
      label,
      onClick,
      isSelected,
      checkedItemCount = 0,
      isBooleanFilter = false,
      isHighlighted: isHighLightedProp = false,
    }: HotFilterButtonProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { t } = useTranslation(['common', 'a11y']);
    const isHighlighted = isHighLightedProp || isSelected || checkedItemCount > 0;
    const textSpanRef = useRef<HTMLSpanElement>(null);
    const [textSpanWidth, setTextSpanWidth] = React.useState<number | undefined>();

    useEffect(() => {
      // this ensures button has the same width when highlighted vs not highlighted
      if (textSpanRef?.current?.offsetWidth > 0) {
        setTextSpanWidth(textSpanRef.current.offsetWidth);
      }
    }, []);

    return (
      <div
        className={styles.hotFilterButtonContainer}
        ref={ref}
      >
        <Button
          data-cy={dataCy}
          disableDefaultStyling
          className={clsx(styles.hotFilterButtonContainer__button, {
            [styles['hotFilterButtonContainer__button--open']]: isHighlighted,
          })}
          id={`hot-filter-button-${label}`}
          aria-expanded={isSelected}
          aria-controls={`hot-filter-content-${label}`}
          onClick={onClick}
        >
          <>
            <span
              ref={textSpanRef}
              style={{ width: textSpanWidth }}
            >
              {t(label)}
            </span>
            {checkedItemCount > 0 && (
              <span className={styles['hotFilterButtonContainer__button--count']}>({checkedItemCount})</span>
            )}
          </>
          {!isBooleanFilter && (
            <Icon
              className={styles.hotFilterButtonContainer__button__icon}
              name={isSelected ? 'small-dropdown-up-default' : 'small-dropdown-down-default'}
              ariaLabel={
                isSelected ? t('A11Y.ICON.DROPDOWN_UP', { ns: 'a11y' }) : t('A11Y.ICON.DROPDOWN_DOWN', { ns: 'a11y' })
              }
            />
          )}
        </Button>
      </div>
    );
  },
);

export default HotFilterButton;
